import { createContext, useContext, useEffect, useState, useRef, ReactNode, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { useApp } from './AppContext';
import { SERVER_URL } from '../api/client';

interface TypingUser {
    userName: string;
    timestamp: number;
}

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    onlineUsers: Set<string>;
    typingUsers: Record<string, TypingUser[]>;
    sendTyping: (containerId: string, type: 'channel' | 'dm' | 'topic') => void;
    stopTyping: (containerId: string, type: 'channel' | 'dm' | 'topic') => void;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) throw new Error('useSocket must be used within SocketProvider');
    return context;
};

export function SocketProvider({ children }: { children: ReactNode }) {
    const {
        currentUser,
        activeView,
        users,
        departments,
        channels,
        directMessages,
        topics,
        tickets,
        projects,
        _setMessages,
        _setChannels,
        _setDirectMessages,
        _setUsers,
        _setTopics,
        _setTickets,
        _setProjects,
        _decryptMessages,
        _setActiveThread,
        loadUnreadCounts,
        loadMessages,
        sendNotification
    } = useApp();

    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
    const [typingUsers, setTypingUsers] = useState<Record<string, TypingUser[]>>({});
    const typingTimeouts = useRef<Record<string, NodeJS.Timeout>>({});

    // Keep users in a ref so stop-typing handler always has fresh data
    // WITHOUT adding users to the socket useEffect dependency (which causes constant re-registration)
    const usersRef = useRef(users);
    useEffect(() => {
        usersRef.current = users;
    }, [users]);

    // Keep a ref to activeView so event handlers always read the latest value
    const activeViewRef = useRef(activeView);
    useEffect(() => {
        activeViewRef.current = activeView;
    }, [activeView]);

    // Keep currentUser refreshed in a ref for socket handlers
    const currentUserRef = useRef(currentUser);
    useEffect(() => {
        currentUserRef.current = currentUser;
    }, [currentUser]);

    // Keep channels in a ref
    const channelsRef = useRef(channels);
    useEffect(() => {
        channelsRef.current = channels;
    }, [channels]);

    // Keep directMessages in a ref
    const directMessagesRef = useRef(directMessages);
    useEffect(() => {
        directMessagesRef.current = directMessages;
    }, [directMessages]);

    // Keep topics in a ref
    const topicsRef = useRef(topics);
    useEffect(() => {
        topicsRef.current = topics;
    }, [topics]);

    // Keep tickets in a ref
    const ticketsRef = useRef(tickets);
    useEffect(() => {
        ticketsRef.current = tickets;
    }, [tickets]);

    // Connect socket when user logs in
    useEffect(() => {
        const token = localStorage.getItem('auth_token');
        if (!token || !currentUser) return;

        console.log('[Socket] Connecting to:', SERVER_URL);
        const newSocket = io(SERVER_URL, {
            auth: { token },
            transports: ['polling', 'websocket'],
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000
        });

        newSocket.on('connect', () => {
            console.log('[Socket] Connected');
            setIsConnected(true);
        });

        newSocket.on('disconnect', (reason) => {
            console.log('[Socket] Disconnected:', reason);
            setIsConnected(false);
        });

        newSocket.on('connect_error', (err) => {
            console.error('[Socket] Connection error:', err.message);
        });

        setSocket(newSocket);

        return () => {
            newSocket.disconnect();
            setSocket(null);
            setIsConnected(false);
        };
    }, [currentUser?.id]);

    // Listen for real-time events
    useEffect(() => {
        if (!socket || !currentUser) return;

        // --- Messages ---
        const handleNewMessage = (message: any) => {
            // Verify user has access to the message's container
            let hasAccess = false;
            if (message.channel_id) {
                hasAccess = channelsRef.current.some((c: any) => c.id === message.channel_id);
            } else if (message.dm_id) {
                hasAccess = directMessagesRef.current.some((d: any) => d.id === message.dm_id);
            } else if (message.topic_id) {
                hasAccess = topicsRef.current.some((t: any) => t.id === message.topic_id);
            } else if (message.ticket_id) {
                hasAccess = ticketsRef.current.some((t: any) => t.id === message.ticket_id);
            }

            if (!hasAccess) return;

            // Always save to local desktop DB for offline cache (regardless of current view)
            if ((window as any).electronAPI) {
                (window as any).electronAPI.saveMessage(message);
            }

            const view = activeViewRef.current;
            const belongsToView =
                (view.type === 'channel' && message.channel_id === view.id) ||
                (view.type === 'dm' && message.dm_id === view.id) ||
                (view.type === 'topic' && message.topic_id === view.id) ||
                (view.type === 'ticket' && message.ticket_id === view.id);

            if (belongsToView) {
                // Decrypt message before inserting into state (handles E2EE messages)
                const [decrypted] = _decryptMessages([message]);

                _setMessages(prev => {
                    const exists = prev.some(m => m.id === message.id);
                    if (exists) {
                        return prev.map(m => m.id === message.id ? decrypted : m);
                    }
                    // For active views, just append it
                    return [...prev, decrypted];
                });

                // If app is not focused (minimized or background), unread counts need updating for the badge
                if (!document.hasFocus()) {
                    loadUnreadCounts();
                }
            } else if (view.type === 'inbox') {
                // If we are currently looking at the inbox, since inbox aggregates multiple 
                // sources and requires complex membership filtering, just reload the messages.
                loadMessages();
            } else {
                // Message outside current view - trigger unread count refresh
                loadUnreadCounts();
            }

            // --- Desktop Notification Trigger ---
            // Don't notify for own messages
            if (message.author_id !== currentUser?.id) {
                const isCurrentView =
                    (view.type === 'channel' && message.channel_id === view.id) ||
                    (view.type === 'dm' && message.dm_id === view.id) ||
                    (view.type === 'topic' && message.topic_id === view.id) ||
                    (view.type === 'ticket' && message.ticket_id === view.id);

                // Show notification if window is not focused OR we are not looking at this conversation
                if (!(document.hasFocus() && isCurrentView)) {
                    const author = usersRef.current.find((u: any) => u.id === message.author_id);
                    // Decrypt message content before displaying in notification
                    const [decryptedMsg] = _decryptMessages([message]);
                    
                    sendNotification(`New message from ${author?.name || 'User'}`, {
                        body: decryptedMsg.content,
                        icon: author?.avatar || undefined,
                        silent: false
                    });
                }
            }

            // --- Thread Updates ---
            // If the message is a reply, update the active thread if it matches
            if (message.parent_id) {
                const [decrypted] = _decryptMessages([message]);
                _setActiveThread(prev => {
                    if (prev && prev.parent.id === message.parent_id) {
                        const exists = prev.replies?.some(r => r.id === message.id);
                        if (exists) {
                            return {
                                ...prev,
                                replies: prev.replies.map(r => r.id === message.id ? decrypted : r)
                            };
                        }
                        return {
                            ...prev,
                            replies: [...(prev.replies || []), decrypted]
                        };
                    }
                    return prev;
                });
            }
        };

        const handleMessageUpdated = (updatedMessage: any) => {

            // Decrypt the updated message (e.g. status changes might echo back ciphertext)
            const [decrypted] = _decryptMessages([updatedMessage]);

            _setMessages(prev => {
                const next = prev.map(m => m.id === updatedMessage.id ? { ...m, ...decrypted } : m);
                const updatedM = next.find(m => m.id === updatedMessage.id);
                if (updatedM && (window as any).electronAPI) {
                    (window as any).electronAPI.saveMessage(updatedM);
                }
                return next;
            });
        };

        // --- Channels ---
        const handleNewChannel = (channel: any) => {
            const currentUserId = currentUserRef.current?.id || currentUser?.id;
            if (channel.is_private &&
                !channel.member_ids?.includes(currentUserId) &&
                !channel.pending_invites?.includes(currentUserId)) {
                return;
            }
            _setChannels(prev => {
                if (prev.some(c => c.id === channel.id)) return prev;
                return [...prev, channel];
            });
        };

        const handleChannelUpdated = (channel: any) => {
            const currentUserId = currentUserRef.current?.id || currentUser?.id;
            _setChannels(prev => {
                if (channel.is_private &&
                    !channel.member_ids?.includes(currentUserId) &&
                    !channel.pending_invites?.includes(currentUserId)) {
                    return prev.filter(c => c.id !== channel.id);
                }
                const exists = prev.some(c => c.id === channel.id);
                if (exists) {
                    return prev.map(c => c.id === channel.id ? { ...c, ...channel } : c);
                }
                return [...prev, channel];
            });
        };

        // --- DMs ---
        const handleNewDM = (dm: any) => {
            const currentUserId = currentUserRef.current?.id || currentUser?.id;
            if (!dm.participant_ids?.includes(currentUserId)) return;
            _setDirectMessages(prev => {
                // Deduplicate: remove any existing DM with same id before adding
                const filtered = prev.filter(d => d.id !== dm.id);
                return [...filtered, dm];
            });
        };

        // --- Topics ---
        const handleTopicUpdated = (topic: any) => {
            const currentUserId = currentUserRef.current?.id || currentUser?.id;
            _setTopics(prev => {
                const exists = prev.some(t => t.id === topic.id);
                if (exists) {
                    if (!topic.member_ids?.includes(currentUserId) &&
                        !topic.pending_invites?.includes(currentUserId)) {
                        return prev.filter(t => t.id !== topic.id);
                    }
                    return prev.map(t => t.id === topic.id ? { ...t, ...topic } : t);
                }
                // If user was just invited, add the topic to their list
                if (topic.member_ids?.includes(currentUserId) ||
                    topic.pending_invites?.includes(currentUserId)) {
                    return [...prev, topic];
                }
                return prev;
            });
        };

        const handleTopicDeleted = (data: { id: string }) => {
            _setTopics(prev => prev.filter(t => t.id !== data.id));
            _setMessages(prev => prev.filter(m => m.topic_id !== data.id));

            // Delete offline cache
            if ((window as any).electronAPI) {
                (window as any).electronAPI.deleteTopicMessages(data.id);
            }
        };

        const handleChannelDeleted = (data: { id: string }) => {
            _setChannels(prev => prev.filter(c => c.id !== data.id));
            _setMessages(prev => prev.filter(m => m.channel_id !== data.id));

            if ((window as any).electronAPI) {
                (window as any).electronAPI.deleteChannelMessages(data.id);
            }
        };

        const handleTicketDeleted = (data: { id: string }) => {
            _setTickets(prev => prev.filter(ticket => ticket.id !== data.id));
            _setProjects(prev => prev.map(project => {
                const ticketIds = project.ticket_ids.filter(id => id !== data.id);
                return { ...project, ticket_ids: ticketIds, ticket_count: ticketIds.length };
            }));
            _setMessages(prev => prev.filter(m => m.ticket_id !== data.id));

            if ((window as any).electronAPI) {
                (window as any).electronAPI.deleteTicketMessages(data.id);
            }
        };

        const handleTicketUpdated = (updatedTicket: any) => {
            const current = currentUserRef.current;
            if (!current) return;
            const rolePermissions = current.role?.permissions || [];
            const isPrivileged = rolePermissions.includes('admin:full') || rolePermissions.includes('super:admin');
            const isCreator = updatedTicket.created_by === current.id;
            const isAssignee = updatedTicket.assignees?.some((assignee: any) => assignee.id === current.id);
            const isCollaborator = updatedTicket.collaborators?.some((collaborator: any) => collaborator.id === current.id);
            const isDepartmentHead = departments.some(department =>
                [updatedTicket.department_id, updatedTicket.assigned_to_department_id].includes(department.id) &&
                department.head_ids?.includes(current.id)
            );
            const isProjectMember = updatedTicket.projects?.some((link: any) =>
                projects.some(project => project.id === link.project_id)
            );
            const canKeepTicket = isPrivileged || isCreator || isAssignee || isCollaborator || isDepartmentHead || isProjectMember;

            _setTickets(prev => {
                if (!canKeepTicket) return prev.filter(ticket => ticket.id !== updatedTicket.id);
                const exists = prev.some(ticket => ticket.id === updatedTicket.id);
                return exists
                    ? prev.map(ticket => ticket.id === updatedTicket.id ? { ...ticket, ...updatedTicket } : ticket)
                    : [...prev, updatedTicket];
            });
            _setProjects(prev => prev.map(project => {
                const belongs = updatedTicket.projects?.some((link: any) => link.project_id === project.id) === true;
                const ticketIds = new Set(project.ticket_ids);
                if (belongs && canKeepTicket) ticketIds.add(updatedTicket.id);
                else ticketIds.delete(updatedTicket.id);
                const nextIds = [...ticketIds];
                return { ...project, ticket_ids: nextIds, ticket_count: nextIds.length };
            }));
        };

        // --- Presence ---
        const handleOnlineUsers = (userIds: string[]) => {
            setOnlineUsers(new Set(userIds));
            _setUsers(prev =>
                prev.map(u => ({
                    ...u,
                    status_type: userIds.includes(u.id) ? 'online' : (u.status_type || 'offline')
                } as any))
            );
        };

        const handlePresenceUpdate = (data: { userId: string; status_type: string; status_text?: string }) => {
            setOnlineUsers(prev => {
                const next = new Set(prev);
                if (data.status_type === 'offline') next.delete(data.userId);
                else next.add(data.userId);
                return next;
            });

            _setUsers(prev =>
                prev.map(u =>
                    u.id === data.userId
                        ? { ...u, status_type: data.status_type as any, status_text: data.status_text }
                        : u
                )
            );
        };

        const handleUserUpdated = (updatedUser: any) => {
            _setUsers(prev =>
                prev.map(u => u.id === updatedUser.id ? { ...u, ...updatedUser } : u)
            );
        };

        // --- Typing ---
        const handleUserTyping = (data: { userId: string; userName: string; channelId?: string; dmId?: string; topicId?: string }) => {
            if (data.userId === currentUser.id) return;
            const containerId = data.channelId || data.dmId || data.topicId;
            if (!containerId) return;

            setTypingUsers(prev => {
                const existing = prev[containerId] || [];
                // Update or add typing user
                const filtered = existing.filter(t => t.userName !== data.userName);
                return {
                    ...prev,
                    [containerId]: [...filtered, { userName: data.userName, timestamp: Date.now() }]
                };
            });

            // Auto-clear after 4 seconds if no stop event received
            const timeoutKey = `${containerId}-${data.userId}`;
            if (typingTimeouts.current[timeoutKey]) {
                clearTimeout(typingTimeouts.current[timeoutKey]);
            }
            typingTimeouts.current[timeoutKey] = setTimeout(() => {
                setTypingUsers(prev => {
                    const existing = prev[containerId] || [];
                    return {
                        ...prev,
                        [containerId]: existing.filter(t => t.userName !== data.userName)
                    };
                });
            }, 4000);
        };

        const handleUserStopTyping = (data: { userId: string; channelId?: string; dmId?: string; topicId?: string }) => {
            const containerId = data.channelId || data.dmId || data.topicId;
            if (!containerId) return;

            const user = usersRef.current.find(u => u.id === data.userId);
            if (!user) return;

            setTypingUsers(prev => {
                const existing = prev[containerId] || [];
                return {
                    ...prev,
                    [containerId]: existing.filter(t => t.userName !== user.name)
                };
            });

            const timeoutKey = `${containerId}-${data.userId}`;
            if (typingTimeouts.current[timeoutKey]) {
                clearTimeout(typingTimeouts.current[timeoutKey]);
                delete typingTimeouts.current[timeoutKey];
            }
        };

        // Register all listeners
        socket.on('new_message', handleNewMessage);
        socket.on('message_updated', handleMessageUpdated);
        socket.on('new_channel', handleNewChannel);
        socket.on('channel_updated', handleChannelUpdated);
        socket.on('new_dm', handleNewDM);
        socket.on('topic_updated', handleTopicUpdated);
        socket.on('topic_deleted', handleTopicDeleted);
        socket.on('channel_deleted', handleChannelDeleted);
        socket.on('ticket_deleted', handleTicketDeleted);
        socket.on('ticket_updated', handleTicketUpdated);
        socket.on('online_users', handleOnlineUsers);
        socket.on('presence_update', handlePresenceUpdate);
        socket.on('user_typing', handleUserTyping);
        socket.on('user_stop_typing', handleUserStopTyping);
        socket.on('user_updated', handleUserUpdated);

        return () => {
            socket.off('new_message', handleNewMessage);
            socket.off('message_updated', handleMessageUpdated);
            socket.off('new_channel', handleNewChannel);
            socket.off('channel_updated', handleChannelUpdated);
            socket.off('new_dm', handleNewDM);
            socket.off('topic_updated', handleTopicUpdated);
            socket.off('topic_deleted', handleTopicDeleted);
            socket.off('channel_deleted', handleChannelDeleted);
            socket.off('ticket_deleted', handleTicketDeleted);
            socket.off('ticket_updated', handleTicketUpdated);
            socket.off('online_users', handleOnlineUsers);
            socket.off('presence_update', handlePresenceUpdate);
            socket.off('user_typing', handleUserTyping);
            socket.off('user_stop_typing', handleUserStopTyping);
            socket.off('user_updated', handleUserUpdated);

            // Clear all typing timeouts
            Object.values(typingTimeouts.current).forEach(clearTimeout);
            typingTimeouts.current = {};
        };
    }, [socket, currentUser?.id, _setMessages, _setChannels, _setDirectMessages, _setUsers, _setTopics, _decryptMessages]);

    // Send typing event (debounced by component)
    const sendTyping = useCallback((containerId: string, type: 'channel' | 'dm' | 'topic') => {
        if (!socket) return;
        const payload: any = {};
        if (type === 'channel') payload.channelId = containerId;
        else if (type === 'dm') payload.dmId = containerId;
        else if (type === 'topic') payload.topicId = containerId;
        socket.emit('typing', payload);
    }, [socket]);

    // Send stop typing event
    const stopTyping = useCallback((containerId: string, type: 'channel' | 'dm' | 'topic') => {
        if (!socket) return;
        const payload: any = {};
        if (type === 'channel') payload.channelId = containerId;
        else if (type === 'dm') payload.dmId = containerId;
        else if (type === 'topic') payload.topicId = containerId;
        socket.emit('stop_typing', payload);
    }, [socket]);

    return (
        <SocketContext.Provider value={{ socket, isConnected, onlineUsers, typingUsers, sendTyping, stopTyping }}>
            {children}
        </SocketContext.Provider>
    );
}
